<script lang="ts">
export interface CreateTemplate {
	id: string;
	name: string;
	icon?: string;
	description?: string;
}
</script>

<script lang="ts" setup>
import { shallowRef, useId, watch } from 'vue';

import { useTranslationUtils } from '#layers/i18n';

const props = defineProps<{
	title: string;
	templates: CreateTemplate[];
	disabled?: boolean;
	loading?: boolean;
}>();

const emit = defineEmits<{
	close: [];
	templateSelected: [CreateTemplate];
}>();

const selectedTemplate = shallowRef<CreateTemplate | undefined>(props.templates[0]);

watch(selectedTemplate, (template) => {
	if (template) {
		emit('templateSelected', template);
	}
}, {
	immediate: true,
});

const formId = useId();

const { translateString } = useTranslationUtils();
</script>

<template>
	<DModal
		:ui="{
			content: 'lg:h-[756px] lg:w-[calc(100vw-6rem)] lg:max-w-[1100px]',
		}"
	>
		<template #content>
			<DUnstyledStepper
				:total-steps="2"
				sync-with-route
				:default-value="templates.length <= 1 ? 2 : 1"
			>
				<template #step-1="{ next }">
					<DCard
						:ui="{
							root: 'flex h-full flex-col',
							body: 'flex-1',
							footer: 'flex items-center justify-end gap-2',
						}"
					>
						<template #header>
							<h2 class="text-xl font-medium">
								{{ title }}
							</h2>
						</template>

						<div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
							<DButton
								v-for="template in templates"
								:key="template.id"
								v-bind="selectedTemplate === template ? {
									color: 'primary',
									variant: 'subtle',
								} : {
									color: 'neutral',
									variant: 'outline',
								}"
								class="rounded-xl p-3"
								@click="() => {
									selectedTemplate = template;
									next();
								}"
							>
								<div class="flex w-full flex-col gap-3 text-start">
									<div class="flex h-[200px] items-center justify-center rounded-lg bg-muted">
										<DIcon :name="template.icon || 'lucide:plus'" class="size-9 text-muted" />
									</div>
									<div>
										<div>
											{{ translateString(template.name) }}
										</div>
										<div v-if="template.description" class="text-sm text-muted">
											{{ translateString(template.description) }}
										</div>
									</div>
								</div>
							</DButton>
						</div>

						<template #footer>
							<DButton
								:label="$t('common.cancel')"
								color="neutral"
								variant="ghost"
								@click="emit('close')"
							/>

							<DButton
								:label="$t('common.continue')"
								color="primary"
								:disabled="!selectedTemplate"
								@click="next()"
							/>
						</template>
					</DCard>
				</template>
				<template #step-2="{ prev }">
					<DCard
						v-if="selectedTemplate"
						:ui="{
							root: 'flex h-full flex-col',
							header: 'flex items-center gap-2',
							body: 'flex-1',
							footer: 'flex items-center justify-end gap-2',
						}"
					>
						<template #header>
							<DButton
								v-if="templates.length > 1"
								icon="lucide:arrow-left"
								color="neutral"
								variant="ghost"
								@click="prev()"
							/>

							<h2 class="text-xl font-medium">
								{{ title }}
							</h2>

							<h3 class="text-xl font-medium text-muted">
								{{ translateString(selectedTemplate.name) }}
							</h3>
						</template>

						<slot
							name="form"
							:form-id
							:selected-template
						/>

						<template #footer>
							<DButton
								:label="$t('common.back')"
								:form="formId"
								color="neutral"
								variant="ghost"
								type="reset"
								@click="prev()"
							/>

							<DButton
								:label="$t('common.create')"
								:form="formId"
								color="primary"
								type="submit"
								:disabled
								:loading
							/>
						</template>
					</DCard>
				</template>
			</DUnstyledStepper>
		</template>
	</DModal>
</template>
